import {Detail, Icon, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {groupBy} from 'lodash';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {EVENT_ID, subscribe, unsubscribe} from '../../../../containers/event_manager';
import type {NovaAgentSummary} from './novaAgentSummary';
import {getAvailableAgents, subscribeToAvailableAgents} from './novaInvocationStore';

export interface NovaAgentPickerProps {
	taskId: string;
	// Disambiguates two mounts of the *same* task's control on one page (e.g. My Work renders a
	// starred task in both its favourited-cards section and its regular task-row section) — each
	// caller site passes its own stable string ('card', 'my-work-row', ...). Omitted, this is a
	// no-op: the caret id and toggle-event key are exactly what they were before this existed, so
	// every other mounting surface (with only one instance per task on the page) is unaffected.
	instanceKey?: string;
	// Which edge of the caret the list lines up with. Defaults to 'left', which is what every
	// surface except the task modal's header wants - see NovaAgentPickerAlign.
	align?: NovaAgentPickerAlign;
	// The instance whose control this list hangs from, when that is not the instance that owns the
	// list. The conversation's Change agent button opens its own picker - it carries a data-loss
	// confirmation the header's picker deliberately does not - but the design shows that list under
	// the header's control, not beside the button deep in the thread. Defaults to its own instance,
	// which is every other surface.
	anchorInstanceKey?: string;
	onSelectAgent: (agentId: string) => void;
}

interface AgentCategoryGroup {
	category: string;
	agents: NovaAgentSummary[];
}

interface PopoverPosition {
	top: number;
	left: number;
	// How tall the list may be where it has been placed. The cap below is what the design draws;
	// this is what the space actually allows, which is smaller near the bottom of a task modal.
	maxHeight: number;
}

function groupByCategory(agents: NovaAgentSummary[]): AgentCategoryGroup[] {
	// Object key insertion order preserves each category's first-seen position. The list arrives
	// with the Default category first, then by category, then Title (see
	// NovaAgentLogic.getAgentsForCustomer), so Default heads the list and the order stays the same
	// between fetches.
	return Object.entries(groupBy(agents, agent => agent.category)).map(([category, categoryAgents]) => ({
		category,
		agents: categoryAgents,
	}));
}

// A single string key naming one control instance — just taskId when there's only one mount of
// that task on the page (the common case, and byte-identical to this project's original,
// pre-instanceKey format), or taskId scoped by instanceKey when a caller says there could be more
// than one. NovaInvocationControl.tsx imports this rather than building its own copy of the
// format, so the caret's id and this picker's lookup of it can never drift apart.
export function novaInstanceKey(taskId: string, instanceKey?: string): string {
	return instanceKey ? `${taskId}:${instanceKey}` : taskId;
}

// The caret that opens this picker (NovaInvocationControl) gives it a stable id to find via the
// DOM rather than a prop-drilled ref, since the two components are deliberately decoupled and
// only communicate over the event bus.
export function anchorElementId(taskId: string, instanceKey?: string): string {
	return `nova-invocation-caret-${novaInstanceKey(taskId, instanceKey)}`;
}

// The control as a whole, which is mounted in every state. The caret above is not: Working, Ready
// and Continue render no caret at all, so a list positioned against it alone would have nothing to
// measure and would land in the page's top-left corner.
export function controlElementId(taskId: string, instanceKey?: string): string {
	return `nova-invocation-control-${novaInstanceKey(taskId, instanceKey)}`;
}

// Which edge of the caret the popover lines up with. 'left' hangs it rightwards, which is what a
// control at the start of a table row or a card wants. 'right' hangs it leftwards, for a control
// sitting at the right edge of a bounded surface - the task modal's header - where opening
// rightwards would run the list past that surface and over the page behind it.
export type NovaAgentPickerAlign = 'left' | 'right';

// The design draws the list at two sizes. The narrower one hangs from a row or card control; the
// wider, taller one opens under the task modal's header, which has the room for it. Both are fixed
// numbers rather than content-sized: the position is computed before layout, so a list that sized
// to its content could not be right-aligned at all, and beside a row control one grew wider than
// the design draws it. The height is a cap, not a size - the list runs to a dozen agents across
// five categories and scrolls past it.
const ROW_WIDTH = 300;
const ROW_MAX_HEIGHT = 316;
const ROW_PADDING = 7;
const HEADER_WIDTH = 330;
const HEADER_MAX_HEIGHT = 392;
const HEADER_PADDING = 8;

// Optional chaining for the same reason the other pieces of this feature carry it: this module is
// reached transitively through the shared web-components barrel, so a jest suite that mocks
// '@forecast-it/design-system' without providing tokensForThemeProvider - and most do not spread
// requireActual - would otherwise throw on import alone, in a suite that has nothing to do with
// Nova.
const ref = tokensForThemeProvider?.sky?.ref?.color;
const sys = tokensForThemeProvider?.sky?.sys?.color;
const size = tokensForThemeProvider?.sky?.sys?.size;
const font = tokensForThemeProvider?.sky?.sys?.font;

const SURFACE = sys?.background.neutral['1'] ?? '';
const HIGHLIGHT = sys?.background.primary['1'] ?? '';
const TEXT_MAIN = sys?.text.content.main ?? '';
const CATEGORY_TEXT = ref?.neutral['400'] ?? '';
const FIELD_BORDER = sys?.border.decorative ?? '';
const DETAIL_1 = font?.detail['1'] ?? '';
const DETAIL_2 = font?.detail['2'] ?? '';
const RADIUS_3 = size?.borderradius['3'] ?? '';
const RADIUS_4 = size?.borderradius['4'] ?? '';
const ROW_HOVER = sys?.background.neutral['2'] ?? '';
// A step up from the resting highlight and still light enough to read the agent's name on.
// background.primary['2'] is the next step the palette has, and it is a solid mid-blue - a
// selected row would turn into a button on hover.
const HIGHLIGHT_HOVER = ref?.blue['100'] ?? '';
const BORDER_ROW = ref?.blue['100'] ?? '';
const BORDER_HEADER = ref?.blue['50'] ?? '';
const ICON_PRIMARY = sys?.iconfill.primary ?? '';

// Which pickers are on screen right now, keyed exactly as NOVA_TOGGLE_PICKER is. A control that
// aims its caret at another surface's picker - the task modal's header, which routes to the
// conversation's own list so that changing the agent warns before discarding the thread - has no
// other way to find out whether that surface is mounted: the two live in different parts of the
// modal, and the one that is absent renders nothing to ask. Dispatching at a key nobody listens on
// is silent, so without this the caret simply did nothing.
//
// Counted rather than a flag: two pickers can legitimately share a key for a moment while React
// swaps one subtree for another, and a plain delete on the first unmount would report the surviving
// one as gone.
const mountedPickerCounts = new Map<string, number>();

// Listeners are keyed the same way the counts are, so a picker mounting or unmounting only wakes
// the controls that asked about that exact key. One flat set woke all of them: on a board or a task
// table every row mounts its own picker, so opening or closing any one of them re-rendered every
// control on screen - including the rows, which aim their caret at their own list and never ask
// this question at all.
const pickerPresenceListeners = new Map<string, Set<() => void>>();

function isKeyMounted(key: string): boolean {
	return (mountedPickerCounts.get(key) ?? 0) > 0;
}

// Which picker is open right now, keyed exactly as the counts above are. The caret that opens a
// list turns to point at it, and the caret is a different component in a different part of the
// tree: it cannot read the list's state, and the list closes by routes the caret never handles - a
// click outside it, Escape, a scroll. Published here so the caret answers about the list as it is
// rather than about the last press it happened to handle.
//
// A set rather than the counter the mounts use: two pickers can share a key for a moment while
// React swaps one subtree for another, but only one list is ever open, so there is nothing to count.
const openPickerKeys = new Set<string>();
const pickerOpenListeners = new Map<string, Set<() => void>>();

function setPickerOpen(key: string, isOpen: boolean): void {
	if (isOpen === openPickerKeys.has(key)) return;
	if (isOpen) openPickerKeys.add(key);
	else openPickerKeys.delete(key);
	pickerOpenListeners.get(key)?.forEach(listener => listener());
}

// Re-renders the caller whenever the picker at one key opens or closes. Unlike useIsPickerMounted,
// an absent instanceKey is an ordinary question rather than a caller with nothing to ask: every
// caret wants this answer, and most of them open their own list, whose key is the task's alone.
export function useIsPickerOpen(taskId: string, instanceKey?: string): boolean {
	const [, bumpOpenVersion] = useState(0);
	const key = novaInstanceKey(taskId, instanceKey);
	const isOpen = openPickerKeys.has(key);

	// What this render committed, for the same reason the presence hook keeps one: the effect below
	// runs once per key, not once per render.
	const openAtLastRenderRef = useRef(isOpen);
	openAtLastRenderRef.current = isOpen;

	useEffect(() => {
		const listener = (): void => bumpOpenVersion(version => version + 1);
		const listeners = pickerOpenListeners.get(key) ?? new Set<() => void>();
		listeners.add(listener);
		pickerOpenListeners.set(key, listeners);
		// The same gap the presence hook closes: the answer is read during render and this
		// subscription starts after the commit, so a list that opened in between told nobody.
		if (openPickerKeys.has(key) !== openAtLastRenderRef.current) listener();
		return () => {
			listeners.delete(listener);
			if (listeners.size === 0) pickerOpenListeners.delete(key);
		};
	}, [key]);

	return isOpen;
}

function notifyPickerPresence(key: string): void {
	pickerPresenceListeners.get(key)?.forEach(listener => listener());
}

// Re-renders the caller whenever the picker at one key mounts or unmounts, so a control deciding
// whether to show a caret is answering about the picker that exists now rather than the one that
// existed when it last rendered.
//
// instanceKey names the picker to watch, and is the only reason to watch at all: a control that
// passes none aims its caret at its own list, which it renders itself and so already knows about.
// Those callers subscribe to nothing and are told false - the answer they were discarding anyway.
export function useIsPickerMounted(taskId: string, instanceKey?: string): boolean {
	const [, bumpPresenceVersion] = useState(0);
	const key = instanceKey ? novaInstanceKey(taskId, instanceKey) : null;
	const mounted = key !== null && isKeyMounted(key);

	// What this render committed. The effect below runs once per key, not once per render, so it
	// cannot close over the value itself without going stale on the second render onwards.
	const mountedAtLastRenderRef = useRef(mounted);
	mountedAtLastRenderRef.current = mounted;

	useEffect(() => {
		if (key === null) return undefined;
		const listener = (): void => bumpPresenceVersion(version => version + 1);
		const listeners = pickerPresenceListeners.get(key) ?? new Set<() => void>();
		listeners.add(listener);
		pickerPresenceListeners.set(key, listeners);
		// The answer is read during render but this subscription starts after the commit, so a
		// picker that mounted in between announced itself to nobody. Re-reading now catches it. Today
		// the header's control always commits before the thread's picker does, so the gap never
		// opened - but that is a fact about this one tree's order, not something this hook promises.
		if (isKeyMounted(key) !== mountedAtLastRenderRef.current) listener();
		return () => {
			listeners.delete(listener);
			if (listeners.size === 0) pickerPresenceListeners.delete(key);
		};
	}, [key]);

	return mounted;
}

// Card/row layouts clip or reflow absolutely-positioned children placed inline with their own
// content — an in-flow popover here would overlap the task name/deadline text directly beneath
// the caret. Portaling to document.body and positioning from the anchor's real screen coordinates
// makes the popover float independently of whatever container mounts this control.
// The caret first, then the control it belongs to: the caret is the exact thing the list drops from
// where it exists, and the control is where it does not (Working, Ready and Continue render none).
function anchorFor(taskId: string, instanceKey?: string): HTMLElement | null {
	return (
		document.getElementById(anchorElementId(taskId, instanceKey)) ??
		document.getElementById(controlElementId(taskId, instanceKey))
	);
}

// The gap the list keeps from the window's own edges when the control it hangs from is too close
// to one for the list to fit beside it.
const VIEWPORT_MARGIN = 8;

/**
 * The given left edge, moved just far enough to keep a popover of this width on screen.
 *
 * The list is a fixed width hung from a control that can sit anywhere - a sprint row beside the
 * side backlog, a backlog card in a narrow window - so left-aligning it to that control alone runs
 * it past the right edge and cuts off the agents at the bottom of the list. Positions are in
 * document coordinates, so the window's own edges are offset by the scroll.
 */
function clampToViewport(left: number, width: number): number {
	const rightLimit = window.scrollX + window.innerWidth - width - VIEWPORT_MARGIN;
	const leftLimit = window.scrollX + VIEWPORT_MARGIN;
	// leftLimit last, so a window narrower than the list itself keeps its left edge visible rather
	// than its right.
	return Math.max(leftLimit, Math.min(left, rightLimit));
}

/**
 * What the list has to stay inside.
 *
 * The viewport, unless the control it hangs from is in a task modal - then the modal, because a
 * list opened from a control deep in a conversation would otherwise run out of the bottom of the
 * modal and float over the page behind it, which reads as belonging to that page rather than to
 * the thread it came from.
 */
function boundsFor(anchor: HTMLElement): {top: number; bottom: number} {
	// The panel, not the backdrop it sits on: `.task-modal-v3` is the full-screen overlay, so its
	// box is the window and would constrain nothing.
	const modal = anchor.closest('.task-task-modal-v3-content');
	if (modal) {
		const box = modal.getBoundingClientRect();
		return {top: box.top + VIEWPORT_MARGIN, bottom: box.bottom - VIEWPORT_MARGIN};
	}
	return {top: VIEWPORT_MARGIN, bottom: window.innerHeight - VIEWPORT_MARGIN};
}

/**
 * Where the list sits vertically, and how tall it may be there.
 *
 * Below its control by default. Above it when there is more room that way, which near the bottom
 * of a modal there is. Either way the height is capped at what the side it landed on can actually
 * give it, so the list scrolls inside its own box rather than past the edge of the surface it
 * belongs to.
 */
function placeVertically(rect: DOMRect, bounds: {top: number; bottom: number}, cap: number) {
	const below = bounds.bottom - rect.bottom - 4;
	const above = rect.top - bounds.top - 4;
	if (below >= Math.min(cap, above)) {
		return {top: rect.bottom + window.scrollY + 4, maxHeight: Math.min(cap, below)};
	}
	const height = Math.min(cap, above);
	return {top: rect.top + window.scrollY - 4 - height, maxHeight: height};
}

function computePosition(taskId: string, instanceKey?: string, align: NovaAgentPickerAlign = 'left'): PopoverPosition | null {
	const anchor = anchorFor(taskId, instanceKey);
	if (!anchor) return null;
	const rect = anchor.getBoundingClientRect();
	const cap = align === 'right' ? HEADER_MAX_HEIGHT : ROW_MAX_HEIGHT;
	const {top, maxHeight} = placeVertically(rect, boundsFor(anchor), cap);
	// Right-aligned means the popover's right edge meets the caret's, so it grows back across the
	// surface the caret sits on rather than out of it.
	if (align === 'right') {
		return {top, maxHeight, left: clampToViewport(rect.right + window.scrollX - HEADER_WIDTH, HEADER_WIDTH)};
	}
	// Left-aligned lines up with the whole control - the Action button and the caret beside it -
	// rather than with the caret alone. The design hangs the list from the start of the thing it
	// belongs to; against the caret it began a button's width further right and read as the caret's
	// own menu. Falls back to the caret where the control is the only element on the page, which is
	// what anchorFor already resolved.
	const control = document.getElementById(controlElementId(taskId, instanceKey));
	const left = (control ?? anchor).getBoundingClientRect().left + window.scrollX;
	return {top, maxHeight, left: clampToViewport(left, ROW_WIDTH)};
}

// The line that says what a run of agents has in common ("DEFAULT", "SCOPING & ESTIMATES", ...).
// Set a tier below the names under it and in the quietest grey the palette has, so a reader scans
// the names and reads the label only when they need to place one.
const CategoryLabel = styled.div`
	${DETAIL_2}
	letter-spacing: 0.4px;
	text-transform: uppercase;
	color: ${CATEGORY_TEXT};
	padding: 8px 10px 3px;
`;

// The question the list answers, above the search that narrows it. Indented to the same 10px the
// rows and labels under it carry, so everything in the panel starts on one line.
const PickerHeading = styled.div`
	padding: 5px 10px 3px;
`;

// A plain input rather than the design system's SearchField. SearchField draws a magnifier inside
// the field and a two-pixel ring on focus, and at the top of a 300px panel that one control
// out-weighed the list it filters. This is the field the design draws: a box of the same type as
// the rows, one line tall.
const SearchInput = styled.input`
	box-sizing: border-box;
	width: calc(100% - 6px);
	margin: 2px 3px 4px;
	padding: 7px 10px;
	border: 1px solid ${FIELD_BORDER};
	border-radius: ${RADIUS_3};
	${DETAIL_1}
	color: ${TEXT_MAIN};
`;

// Name and description are one label, so they stack against the tick rather than beside it.
const AgentLabel = styled.span`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	text-align: left;
`;

const AgentTitle = styled.span`
	${DETAIL_1}
	font-weight: 500;
	color: ${TEXT_MAIN};
`;

const AgentDescription = styled.span`
	${DETAIL_2}
	color: ${CATEGORY_TEXT};
	margin-top: 2px;
`;

// Sits at the end of the row it marks, at the size of the name beside it. The glyph inside is the
// design system's own tick rather than the typed character this used to draw, which rendered in
// whatever the platform's default font had and sat a little high against the name beside it.
const CheckMark = styled.span`
	flex: none;
	display: inline-flex;
	align-items: center;
	color: ${ICON_PRIMARY};

	svg {
		width: 16px;
		height: 16px;
	}
`;

// A plain <button> with inline styles (as this row was before) can't express :hover — converted
// to a styled-component for that alone. $highlighted tints the row the same way the mockup
// highlights its current default agent — see the two callers of this ($isAccountDefault and
// $isImplicitChoice below) for why there are two, distinct reasons a row can set it.
const AgentRow = styled.button<{$highlighted: boolean}>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	width: 100%;
	text-align: left;
	border: none;
	border-radius: ${RADIUS_3};
	padding: 8px 10px;
	cursor: pointer;
	background: ${({$highlighted}) => ($highlighted ? HIGHLIGHT : 'transparent')};

	// A highlighted row hovers to a stronger shade of the same blue family, not the plain rows'
	// neutral hover — the unconditional neutral hover this replaced was overriding the highlight
	// background entirely, so hovering the highlighted row visibly flashed it back to white/gray.
	&:hover {
		background: ${({$highlighted}) => ($highlighted ? HIGHLIGHT_HOVER : ROW_HOVER)};
	}
`;

function popoverStyle(position: PopoverPosition, align: NovaAgentPickerAlign = 'left'): React.CSSProperties {
	const underHeader = align === 'right';
	return {
		position: 'absolute',
		top: position.top,
		left: position.left,
		zIndex: 1000,
		width: underHeader ? HEADER_WIDTH : ROW_WIDTH,
		// What the space allows where it landed, never more than the design's own cap.
		maxHeight: position.maxHeight,
		overflowY: 'auto',
		background: SURFACE,
		// Outlined in the conversation's own blue rather than the neutral line the rest of the page
		// is boxed in, so the list reads as part of the feature the control belongs to. A step
		// lighter under the modal header, where it opens over the header's own tint and the firmer
		// line would draw a box around nothing.
		border: `1px solid ${underHeader ? BORDER_HEADER : BORDER_ROW}`,
		borderRadius: RADIUS_4,
		// The design system has no floating-popover shadow: its two elevation tokens are the flat
		// drop a button or card carries, and neither lifts a list off the page behind it. This soft
		// blur is a deliberate one-off rather than a token used for something it is not.
		boxShadow: '0 4px 16px rgba(20,20,40,0.16)',
		padding: underHeader ? HEADER_PADDING : ROW_PADDING,
	};
}

export const NovaAgentPicker = (props: NovaAgentPickerProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [position, setPosition] = useState<PopoverPosition | null>(null);
	const popoverRef = useRef<HTMLDivElement>(null);
	// The store, not this component's own state, is the source of truth for the agent list. This
	// counter's value is never read for itself — bumping it forces the re-render (and the groups
	// memo below, which depends on it) whenever the store changes underneath an already-mounted
	// picker, e.g. a slow fetch resolving after the picker opened.
	const [agentsVersion, setAgentsVersion] = useState(0);

	useEffect(() => subscribeToAvailableAgents(() => setAgentsVersion(n => n + 1)), []);

	// Announces whether this picker is open, under its own key. See openPickerKeys above.
	useEffect(() => {
		const key = novaInstanceKey(props.taskId, props.instanceKey);
		setPickerOpen(key, open);
		return () => setPickerOpen(key, false);
	}, [open, props.taskId, props.instanceKey]);

	// Announces this picker's key for as long as it is mounted. See mountedPickerCounts above.
	useEffect(() => {
		const key = novaInstanceKey(props.taskId, props.instanceKey);
		mountedPickerCounts.set(key, (mountedPickerCounts.get(key) ?? 0) + 1);
		notifyPickerPresence(key);
		return () => {
			const remaining = (mountedPickerCounts.get(key) ?? 1) - 1;
			if (remaining > 0) mountedPickerCounts.set(key, remaining);
			else mountedPickerCounts.delete(key);
			notifyPickerPresence(key);
		};
	}, [props.taskId, props.instanceKey]);

	// A portaled popover with nothing listening for an outside click never closes on its own — the
	// only close path otherwise is re-clicking the caret. Runs on mousedown, not click: the same
	// physical click that opens the picker (via the caret's own onClick, over the event bus) would
	// otherwise be seen as an "outside" click by a same-tick click listener and close it right back.
	useEffect(() => {
		if (!open) return undefined;
		const handlePointerDown = (event: MouseEvent): void => {
			const target = event.target as Node;
			// The caret's own click already toggles via the event bus above — let that be the only
			// handler for it, or this listener and the toggle would race and reopen what it just closed.
			if (anchorFor(props.taskId, props.anchorInstanceKey ?? props.instanceKey)?.contains(target)) return;
			if (anchorFor(props.taskId, props.instanceKey)?.contains(target)) return;
			if (popoverRef.current?.contains(target)) return;
			setOpen(false);
			setQuery('');
		};
		document.addEventListener('mousedown', handlePointerDown);
		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [open, props.taskId, props.instanceKey, props.anchorInstanceKey]);

	useEffect(() => {
		if (!open) return undefined;
		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape') {
				setOpen(false);
				setQuery('');
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [open]);

	// Position is computed once at open time from the anchor's real screen coordinates and never
	// updated after — scrolling any ancestor (e.g. the scoping table's own inner scroll container)
	// moves the caret without moving this portaled popover, leaving it visually detached. Closing
	// on scroll is simpler and safer than tracking position live, and matches how a native <select>
	// dropdown behaves. The capture phase is required: a nested container's own scroll event does
	// not bubble to document, only capture-phase listeners on an ancestor see it — but that also
	// means the popover's own agent list (overflowY: auto) reports its internal scroll the same
	// way, so a scroll targeting the popover itself is excluded rather than closing on its own list.
	useEffect(() => {
		if (!open) return undefined;
		const handleScroll = (event: Event): void => {
			if (popoverRef.current?.contains(event.target as Node)) return;
			setOpen(false);
			setQuery('');
		};
		document.addEventListener('scroll', handleScroll, true);
		return () => document.removeEventListener('scroll', handleScroll, true);
	}, [open]);

	useEffect(() => {
		// Reads `open` directly (a plain event-handler closure, not a setState updater) rather than
		// deriving it via a functional setOpen update — computePosition/setPosition are side
		// effects, and calling side effects from inside a state updater isn't safe: React may
		// invoke an updater more than once for the same update.
		const handler = (toggledKey: string): void => {
			if (toggledKey !== novaInstanceKey(props.taskId, props.instanceKey)) return;
			const next = !open;
			if (next)
				setPosition(
					// Falls back to this list's own control when the one it was told to hang from is not
					// mounted, so it never lands in the corner with nothing to measure.
					computePosition(props.taskId, props.anchorInstanceKey ?? props.instanceKey, props.align) ??
						computePosition(props.taskId, props.instanceKey, props.align)
				);
			setOpen(next);
			setQuery('');
		};
		subscribe<string>(EVENT_ID.NOVA_TOGGLE_PICKER, handler);
		return () => unsubscribe<string>(EVENT_ID.NOVA_TOGGLE_PICKER, handler);
	}, [props.taskId, props.instanceKey, props.anchorInstanceKey, props.align, open]);

	// A bare Action click runs whichever agent is first in the resolved list (see
	// useNovaInvocationState's onAction). The list arrives with the Default category first, so that is
	// the first Default agent the viewer can access, or the first agent of all when they can access
	// none. Highlighting that same row shows what a bare Action click will do. It is not the
	// account-default flag (isDefault), which the backend still hardcodes to false.
	const firstAgentId = useMemo(() => getAvailableAgents()[0]?.id, [agentsVersion]);

	const groups = useMemo(() => {
		const normalizedQuery = query.trim().toLowerCase();
		const filtered = getAvailableAgents().filter(
			agent =>
				!normalizedQuery ||
				agent.title.toLowerCase().includes(normalizedQuery) ||
				(agent.description ?? '').toLowerCase().includes(normalizedQuery)
		);
		return groupByCategory(filtered);
	}, [query, agentsVersion]);

	if (!open) return null;

	// In jsdom (unit tests), getBoundingClientRect returns an all-zero rect, so position may be
	// null there — {top: 0, left: 0} is a reasonable fallback for that environment and is never
	// visible in a real browser (a real anchor always yields a real rect).
	// The fallback keeps the design's own cap for the surface it is on - in jsdom every rect is
	// zero, so there is no measured space to cap it against.
	const style = popoverStyle(
		position ?? {top: 0, left: 0, maxHeight: props.align === 'right' ? HEADER_MAX_HEIGHT : ROW_MAX_HEIGHT},
		props.align
	);

	const content = (
		<div
			ref={popoverRef}
			role="listbox"
			aria-label={formatMessage({id: 'nova_invocation.picker.aria_label'})}
			data-cy="nova-agent-picker"
			style={style}
		>
			<PickerHeading>
				<Detail color="medium" data-cy="nova-agent-picker-header">
					{formatMessage({id: 'nova_invocation.picker.header'})}
				</Detail>
			</PickerHeading>
			<SearchInput
				value={query}
				onChange={e => setQuery(e.target.value)}
				placeholder={formatMessage({id: 'nova_invocation.picker.search_placeholder'})}
				data-cy="nova-agent-picker-search"
			/>
			{groups.length === 0 && (
				<Text data-cy="nova-agent-picker-empty">{formatMessage({id: 'nova_invocation.picker.empty_state'})}</Text>
			)}
			{groups.map(group => (
				<div key={group.category} role="group" aria-label={group.category}>
					<CategoryLabel>{group.category}</CategoryLabel>
					{group.agents.map(agent => {
						// Two distinct, currently mutually-exclusive reasons a row can be marked: a
						// real account default (not wired up yet — always false today) versus this
						// picker's own stand-in, "the agent a bare Action click would use". Kept as
						// separate booleans rather than one, so wiring up the real default later is a
						// matter of this flag starting to vary, not a redefinition of "highlighted".
						const isAccountDefault = agent.isDefault;
						const isImplicitChoice = !isAccountDefault && agent.id === firstAgentId;
						return (
							<AgentRow
								key={agent.id}
								type="button"
								role="option"
								aria-selected={false}
								$highlighted={isAccountDefault || isImplicitChoice}
								data-cy="nova-agent-picker-row"
								onClick={() => {
									props.onSelectAgent(agent.id);
									setOpen(false);
									setQuery('');
								}}
							>
								{/* The title and its supporting line are one label, so they sit closer
								    together than the rows they distinguish. */}
								<AgentLabel>
									<AgentTitle>{agent.title}</AgentTitle>
									{/* A missing description renders the row without a supporting
									    line rather than an empty element. */}
									{agent.description && <AgentDescription>{agent.description}</AgentDescription>}
								</AgentLabel>
								{(isAccountDefault || isImplicitChoice) && (
									<CheckMark
										aria-label={formatMessage({
											id: isAccountDefault
												? 'nova_invocation.picker.default_agent_marker'
												: 'nova_invocation.picker.implicit_choice_marker',
										})}
										data-cy={
											isAccountDefault
												? 'nova-agent-picker-default-mark'
												: 'nova-agent-picker-implicit-mark'
										}
									>
										<Icon icon="tick" size="s" customColor="currentColor" />
									</CheckMark>
								)}
							</AgentRow>
						);
					})}
				</div>
			))}
		</div>
	);

	return createPortal(content, document.body);
};
